/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as R from "remeda";
import type {
  ActionConfig,
  AutomationConfig,
  ValueConfig,
  ActionReference,
} from "@smartsuite/automation-engine-api";
import {
  AutomationConfig_UserStatus,
  ConditionsLogicOperator,
  RecordListInputConfig_Reference,
  TriggerDescription_TriggerRetrySupport,
} from "@smartsuite/automation-engine-api";
import { makeAutoObservable } from "mobx";
import { v4 as uuid } from "uuid";
import type {
  ActionCandidate,
  ActionGroupCandidate,
  ActionGroupType,
  AutomationCandidate,
  BranchCandidate,
  ElseBranchCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../types/candidate";
import { ejectEmptyInputs, ejectReferences } from "./utils/filters";
import {
  actionValidation,
  candidateTriggerValid,
  candidateValidation,
} from "./utils/inputValidation";
import { enhanceInputs } from "./utils/enhancers";
import { automationValidSchema } from "./utils/validation";
import type { TriggerMerger } from "./mergers/trigger";
import type { ActionListMerger } from "./mergers/actions";
import {
  buildStringInputConfig,
  findInputAndPick,
  pickActionName,
  pickValueConfigString,
} from "../../utils/inputs";
import {
  ActionLabel,
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
} from "../../utils/constants";
import { compareActionReferences } from "../../utils/conditions-coverter.hook";
import { ejectDependValues } from "./utils/eject-depend-values";
import {
  consumesFindRecords,
  consumesTrigger,
  followRecordListSource,
  isPdfAction,
} from "./utils/follow-record-list-source";
import { LoopingStore } from "./looping.store";
import type { LoopScope } from "./looping.store";
import { BranchesStore } from "./branches.store";
import { isFindRecordsOne } from "../../containers/AutomationForm/Forms/InputForm/utils";
import {
  collectActionsDeep,
  countAutomationStepsDeep,
  ensureDirectActionsGroups,
  findGroupByInstanceId,
  findTopLevelGroupByActionHash,
  locateActionByHash,
  locateEnclosingLoop,
  mapActionsDeep,
  stripEmptyActionsGroups,
  walkActionGroups,
} from "./utils/action-group-walk";
import type { GroupLocator } from "./utils/action-group-walk";

export class CandidateStore {
  self: AutomationCandidate;
  looping: LoopingStore;
  branches: BranchesStore;
  isTriggerSubmitted: boolean;
  actionsSubmitted: string[];
  originalActionInstanceIds: number[];
  currentActionGroup: ActionGroupCandidate | undefined;
  currentGroupLocator?: GroupLocator;
  currentActionHash?: string;
  changeActiveActionTo?: {
    hash: string;
  };

  showErrorSection: boolean;
  triggerPermissionsError: string | undefined = undefined;

  constructor() {
    this.self = getEmptyAutomation("", "");
    this.looping = new LoopingStore(this);
    this.branches = new BranchesStore(this);
    this.isTriggerSubmitted = false;
    this.actionsSubmitted = [];
    this.originalActionInstanceIds = [];
    this.showErrorSection = false;
    makeAutoObservable(this);
  }

  // Its a way to set value for action input kind of "manualy"
  setActionInput(
    actionConfig: ActionConfig,
    inputId: string,
    value: ValueConfig,
  ): void {
    const action = this.allActions.find(
      (action) =>
        action.actionReference !== undefined &&
        actionConfig.actionReference !== undefined &&
        compareActionReferences(
          action.actionReference,
          actionConfig.actionReference,
        ),
    );
    if (action !== undefined) {
      const input = action.inputs.find((input) => input.inputId === inputId);
      if (input !== undefined) {
        input.value = value;
      }
    }
  }

  setTriggerPermissionsError(error: string): void {
    this.triggerPermissionsError = error;
  }

  clearTriggerPermissionsError(): void {
    this.triggerPermissionsError = undefined;
  }

  setShowErrorSection(showErrorSection: boolean): void {
    this.showErrorSection = showErrorSection;
  }

  setChangeActiveActionTo(actionHash?: string): void {
    if (actionHash === undefined) {
      this.changeActiveActionTo = undefined;
      return;
    }

    this.changeActiveActionTo = {
      hash: actionHash,
    };
  }

  isNew(): boolean {
    return this.self.automationId === "";
  }

  createEmptyAutomation(solutionId: string, timzezone: string): void {
    this.self = getEmptyAutomation(solutionId, timzezone);
    this.originalActionInstanceIds = [];
    this.setShowErrorSection(false);
  }

  cloneAndSetAutomation(automation: AutomationConfig): void {
    this.originalActionInstanceIds = collectActionsDeep(
      automation.actionGroups as ActionGroupCandidate[],
    )
      .map((action) => action.actionReference?.instanceId)
      .filter((instanceId): instanceId is number => instanceId !== undefined);

    this.self = R.pipe(
      R.clone(automation),
      (automationClone) => ({
        ...automationClone,
        hash: uuid(),
        trigger: {
          ...automationClone.trigger!,
          hash: uuid(),
          description: {
            triggerId: "",
            label: "",
            subLabel: "",
            authenticationId: [],
            inputs: [],
            outputs: [],
            conditionFields: [],
            triggerRetrySupport:
              TriggerDescription_TriggerRetrySupport.RETRY_NOT_SUPPORTED,
          },
          conditions: automationClone.trigger!.conditions ?? {
            operator: ConditionsLogicOperator.OPERATOR_AND,
            group: [],
          },
        },
        actions: [],
        actionGroups: mapActionsDeep(
          automationClone.actionGroups as ActionGroupCandidate[],
          (action) => {
            const actionName = pickActionName(
              action.inputs,
              action.actionReference?.actionId,
            );
            return {
              ...action,
              hash: uuid(),
              description: {
                actionId: "",
                label: "",
                subLabel: "",
                authenticationId: [],
                inputs: [],
                outputs: [],
                conditionFields: [],
                triggerRetrySupport:
                  TriggerDescription_TriggerRetrySupport.RETRY_NOT_SUPPORTED,
              },
              actionName,
            };
          },
        ),
      }),
      (enhancedClone) => ({
        ...enhancedClone,
        actionGroups: mapActionsDeep(enhancedClone.actionGroups, (action) => ({
          ...action,
          inputs: enhanceInputs(action.inputs),
        })),
      }),
      (withInputs) => {
        walkActionGroups(withInputs.actionGroups, (group) => {
          if (group.config.oneofKind === "branches") {
            group.config.branches.branches.forEach((branch) => {
              branch.hash = uuid();
            });
            if (group.config.branches.elseBranch !== undefined) {
              group.config.branches.elseBranch.hash = uuid();
            }
          }
        });
        return withInputs;
      },
      (withHashes) => {
        // Backend-originated configs (and previously-stripped saves, see
        // release()'s stripEmptyActionsGroups) can omit the direct "actions"
        // group a container would otherwise always keep -- re-seed it so
        // setActionsAt/render targets keep working.
        ensureDirectActionsGroups(withHashes.actionGroups);
        return withHashes;
      },
    );

    this.setShowErrorSection(false);
  }

  setTrigger(...mergers: TriggerMerger[]): void {
    this.self.trigger = R.reduce<TriggerMerger, TriggerCandidate>(
      mergers,
      (trigger, merger) => merger(trigger),
      this.self.trigger,
    );
  }

  switchTriggerConditionOperator(): void {
    if (this.self.trigger.conditions !== undefined) {
      this.self.trigger.conditions.operator =
        this.self.trigger.conditions.operator ===
        ConditionsLogicOperator.OPERATOR_AND
          ? ConditionsLogicOperator.OPERATOR_OR
          : ConditionsLogicOperator.OPERATOR_AND;
    }
  }

  setActions(mergers: ActionListMerger[], groupType?: ActionGroupType): void {
    const type = groupType ?? this.currentActionGroup?.config.oneofKind;

    if (type === "branches" || type === "loop") {
      // The locator captured when the action's section was opened is the
      // source of truth for WHERE to write. `type` is derived from the
      // TOP-LEVEL group (findTopLevelGroupByActionHash / currentActionGroup),
      // so for an action inside a loop nested in a branch (or a branch
      // nested in a loop) the two can disagree -- e.g. type === "branches"
      // while currentGroupLocator is {kind: "loop"}. Route by the locator
      // whenever it names a real container, regardless of which container
      // string arrived (Task 5.6).
      if (
        this.currentGroupLocator?.kind === "branch" ||
        this.currentGroupLocator?.kind === "loop"
      ) {
        this.setActionsAt(this.currentGroupLocator, mergers);
        return;
      }
      if (type === "branches") {
        // No container locator set -- ambiguous, safe no-op (pre-existing).
        return;
      }
      // type === "loop" with no container locator falls through to the
      // root-level loop find below (pre-existing fallback).
    }

    const targetGroup = this.self.actionGroups.find(
      (group) => group.config.oneofKind === type,
    );
    if (targetGroup === undefined) return;
    this.setActionsAt(
      type === "loop"
        ? { kind: "loop", instanceId: targetGroup.instanceId }
        : { kind: "root", instanceId: targetGroup.instanceId },
      mergers,
    );
  }

  setActionsAt(locator: GroupLocator, mergers: ActionListMerger[]): void {
    const targetGroup = findGroupByInstanceId(
      this.self.actionGroups,
      locator.instanceId,
    );
    if (targetGroup === undefined) return;

    let targetBranch: BranchCandidate | ElseBranchCandidate | undefined;
    if (targetGroup.config.oneofKind === "branches") {
      if (locator.kind !== "branch") return;
      const { branches } = targetGroup.config;
      targetBranch =
        locator.branchHash === "else"
          ? branches.elseBranch
          : branches.branches.find((b) => b.hash === locator.branchHash);
      if (targetBranch === undefined) return;
    }

    // Seed the merge base from the container's OWN DIRECT "actions" groups
    // only (mirrors BranchesStore.branchActions' shallow filter). Using
    // collectActionsDeep(targetBranch.actionGroups) here would also pull in
    // actions from nested loop/branches groups inside this container --
    // those stay in the nested container too, so writing the merged result
    // back into only the direct "actions" groups below would duplicate them.
    const baseGroups =
      targetBranch !== undefined
        ? targetBranch.actionGroups.filter(
            (group) => group.config.oneofKind === "actions",
          )
        : targetGroup.config.oneofKind === "loop"
          ? targetGroup.config.loop.actionGroups.filter(
              (group) => group.config.oneofKind === "actions",
            )
          : [targetGroup];
    const currentActions = collectActionsDeep(baseGroups);
    const mergedActions = R.reduce<ActionListMerger, ActionCandidate[]>(
      mergers,
      (actions, merger) => merger(actions),
      currentActions,
    );

    // dirty hack to remove "in-app-message-input" input if trigger is schedule
    mergedActions.forEach((action) => {
      if (
        this.self.trigger.triggerReference?.triggerId === "schedule" &&
        action.actionReference?.actionId === "send-email"
      ) {
        action.inputs = action.inputs.filter(
          (input) => input.inputId !== "in-app-message-input",
        );
        action.description.inputs = action.description.inputs.filter(
          (input) => input.inputId !== "in-app-message-input",
        );
      }
    });

    if (targetGroup.config.oneofKind === "actions") {
      targetGroup.config.actions.actions = mergedActions;
    } else if (targetGroup.config.oneofKind === "loop") {
      targetGroup.config.loop.actionGroups.forEach((nested) => {
        if (nested.config.oneofKind === "actions") {
          nested.config.actions.actions = mergedActions;
        }
      });
    } else if (targetGroup.config.oneofKind === "branches") {
      targetBranch?.actionGroups.forEach((nested) => {
        if (nested.config.oneofKind === "actions") {
          nested.config.actions.actions = mergedActions;
        }
      });
    }
  }

  private setActionsByGroup(
    actions: ActionCandidate[],
    groupType: ActionGroupType,
  ): void {
    this.self.actionGroups = this.self.actionGroups.map((actionGroup) => {
      if (
        groupType === "actions" &&
        actionGroup.config.oneofKind === "actions"
      ) {
        actionGroup.config.actions.actions = actions;
      }

      if (groupType === "loop" && actionGroup.config.oneofKind === "loop") {
        return {
          ...actionGroup,
          config: {
            ...actionGroup.config,
            loop: {
              ...actionGroup.config.loop,
              actionGroups: actionGroup.config.loop.actionGroups.map(
                (loopActionGroup) => {
                  if (loopActionGroup.config.oneofKind === "actions") {
                    return {
                      ...loopActionGroup,
                      config: {
                        ...loopActionGroup.config,
                        actions: {
                          ...loopActionGroup.config.actions,
                          actions,
                        },
                      },
                    };
                  }
                  return loopActionGroup;
                },
              ),
            },
          },
        };
      }

      return actionGroup;
    });
  }

  // Delete Action operation
  initDeletedActions(action: ActionConfig, defaultSolutionId: string): void {
    if (["delete-record"].includes(action.actionReference?.actionId ?? "")) {
      const appInput = this.self.trigger.inputs.find(
        (input) => input.inputId === INPUT_APPLICATION_ID,
      );

      if (appInput !== undefined && !action.inputs.length) {
        const restItems = action.inputs.filter(
          (input) =>
            ![INPUT_APPLICATION_ID, INPUT_SOLUTION_ID].includes(input.inputId),
        );

        restItems.push(appInput);
        restItems.push(
          buildStringInputConfig(INPUT_SOLUTION_ID, defaultSolutionId),
        );
        action.inputs = restItems;
      }
    }
  }

  setActionsWithOutReferences(
    reference?: string,
    whatActionReferencesRemove?: ActionReference,
    outputIdToRemove?: string,
  ): void {
    const actions = this.getGroupActions("actions")
      .map((action) =>
        ejectReferences(
          action,
          reference,
          whatActionReferencesRemove,
          outputIdToRemove,
        ),
      )
      .map((action) => {
        if (
          (action.actionReference?.actionId === "gen-pdf" ||
            action.actionReference?.actionId ===
              "docsautomator-generate-pdf") &&
          action.recordList?.recordList !== undefined &&
          action.recordList?.recordList.oneofKind === "actionReference" &&
          whatActionReferencesRemove !== undefined
        ) {
          if (
            compareActionReferences(
              action.recordList.recordList.actionReference,
              whatActionReferencesRemove,
            )
          ) {
            action.recordList = undefined;
            const docInput = action.inputs.find(
              (input) => input.inputId === "export-type",
            );
            if (docInput !== undefined) {
              docInput.value = undefined;
            }
          }
        }

        return action;
      });

    this.setActionsByGroup(actions, "actions");
  }

  setNeedToUpdateDynamicDescription(
    action: ActionCandidate,
    value: boolean,
  ): void {
    this.self.actionGroups = mapActionsDeep(this.self.actionGroups, (a) =>
      a.hash === action.hash
        ? { ...a, needToUpdateDynamicDescription: value }
        : a,
    );
  }

  /**
   * A Find Records action now searches another table: every action that
   * takes its records from it follows, as does every loop over them.
   *
   * Only those actions. An action fed by the trigger, or by another Find
   * Records action, is not affected by this change; clearing it too is what
   * emptied a Generate PDF action whenever any other record source was
   * picked (BUG-22413).
   *
   * An action's record list carries a copy of the table its source searches,
   * made when the source was picked, and the card, the field list and the
   * engine all read that copy. Left alone, it kept naming the old table
   * after the source moved on, and the run updated the old table with the
   * records found in the new one (BUG-22529).
   */
  refreshFindDependenciesByApplicationId(
    findActionWithChangedApplication: ActionCandidate,
    applicationId?: string,
  ): void {
    const changedInstanceId =
      findActionWithChangedApplication.actionReference?.instanceId;
    const actionReference = findActionWithChangedApplication.actionReference;
    if (
      changedInstanceId === undefined ||
      actionReference === undefined ||
      applicationId === undefined ||
      applicationId === ""
    ) {
      return;
    }
    const table = {
      applicationId,
      solutionId:
        findInputAndPick(
          findActionWithChangedApplication.inputs,
          INPUT_SOLUTION_ID,
          pickValueConfigString,
        ) ?? this.self.solutionId,
    };

    // Walk the WHOLE tree (root actions, loops, branches, and any nesting)
    // instead of flatten-per-group + setActionsByGroup, which only refilled a
    // group's DIRECT actions and duplicated nested actions upward.
    this.self.actionGroups = mapActionsDeep(this.self.actionGroups, (action) =>
      consumesFindRecords(action, changedInstanceId)
        ? followRecordListSource(action, table, { actionReference })
        : action,
    );
    this.looping.followSourceTable(changedInstanceId, table);
  }

  /**
   * The trigger now watches another table: every action that takes its
   * records from the trigger follows it (BUG-22529). Actions fed by a Find
   * Records action keep theirs.
   *
   * A trigger with no table yet cannot be followed; the PDF actions fed by
   * it still lose the template they had picked, since a template belongs to
   * a table, and describe themselves again.
   */
  refreshTriggerDependencies(): void {
    const applicationId = findInputAndPick(
      this.self.trigger.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );
    const table =
      applicationId === undefined || applicationId === ""
        ? undefined
        : {
            applicationId,
            solutionId:
              findInputAndPick(
                this.self.trigger.inputs,
                INPUT_SOLUTION_ID,
                pickValueConfigString,
              ) ?? this.self.solutionId,
          };

    this.self.actionGroups = mapActionsDeep(
      this.self.actionGroups,
      (action) => {
        if (!consumesTrigger(action)) {
          return action;
        }
        if (table !== undefined) {
          return followRecordListSource(action, table, { trigger: true });
        }
        if (isPdfAction(action)) {
          action.needToUpdateDynamicDescription = true;
          return ejectDependValues(action);
        }
        return action;
      },
    );
  }

  setSubmitted(): void {
    this.isTriggerSubmitted = true;
    this.actionsSubmitted = collectActionsDeep(this.self.actionGroups).map(
      (action) => action.hash,
    );
  }

  removeSubmitted(): void {
    this.isTriggerSubmitted = false;
    this.actionsSubmitted = [];
  }

  setIsTriggerSubmitted(isSubmitted: boolean): void {
    this.isTriggerSubmitted = isSubmitted;
  }

  isActiveStatus(): boolean {
    return this.self.userStatus === AutomationConfig_UserStatus.USER_ENABLED;
  }

  isCanBeSubmitted(): boolean {
    return candidateValidation(this);
  }

  isTriggerIsRecordUpdatedAndHasNoConditions(): boolean {
    if (
      this.self.trigger.triggerReference?.triggerId === "updated-record" &&
      (this.self.trigger.conditions === undefined ||
        this.self.trigger.conditions.group.length === 0)
    ) {
      return true;
    }
    return false;
  }

  isActionValid(action: ActionCandidate): boolean {
    return actionValidation(action);
  }

  isActionSubmitted(hash: string): boolean {
    return this.actionsSubmitted.includes(hash);
  }

  isNewlyAddedAction(action: ActionCandidate): boolean {
    const instanceId = action.actionReference?.instanceId;

    if (instanceId === undefined) {
      return true;
    }

    return !this.originalActionInstanceIds.includes(instanceId);
  }

  isTriggerValid(): boolean {
    return candidateTriggerValid(this);
  }

  updateStatus(status: AutomationConfig_UserStatus): void {
    this.self.userStatus = status;
  }

  updateLabel(label: string): void {
    this.self.label = label;
  }

  // use for when button is clicked trigger functionality
  setAutomationId(automationId: string): void {
    this.self.automationId = automationId;
  }

  updateDescription(description: string): void {
    this.self.description = description;
  }

  setAutoDescription(description: string): void {
    this.self.automaticDescription = description;
  }

  release(skipError = false): Promise<AutomationConfig> {
    const automation = R.clone(ejectEmptyInputs(this.self));
    // The UI keeps an always-present direct "actions" group per container
    // (setActionsAt's write target); the backend engine rejects an empty one
    // ("Actions group must contain at least one action"). Strip them from the
    // payload here so what gets validated/sent matches the engine's contract.
    automation.actionGroups = stripEmptyActionsGroups(automation.actionGroups);
    if (automation.systemStatus !== undefined && !skipError) {
      automation.systemStatus.errors = [];
    }

    return automationValidSchema
      .passthrough()
      .parseAsync(automation as AutomationConfig)
      .then(() => {
        return automation as AutomationConfig;
      });
  }

  setActionGroupByActionHash(actionHash: string): void {
    this.currentActionGroup = findTopLevelGroupByActionHash(
      this.self.actionGroups,
      actionHash,
    );
    this.currentGroupLocator = locateActionByHash(
      this.self.actionGroups,
      actionHash,
    );
    this.currentActionHash = actionHash;
  }

  /**
   * Forgets which action's panel is open (call it when that panel unmounts),
   * so `currentLoopScope` stops resolving to a loop the user has left -- a
   * trigger condition edited afterwards must not be offered that loop's
   * Current Item. `currentGroupLocator`/`currentActionGroup` are kept on
   * purpose: write routing consumers read them after the panel is gone.
   */
  clearCurrentAction(): void {
    this.currentActionHash = undefined;
  }

  /**
   * Scope of the loop ENCLOSING the action whose panel is open, resolved past
   * branch containers -- the read every "is this action inside a loop" gate
   * (Current Item menu entry, current-item pill insertion, math operators)
   * must use. `currentGroupLocator` is the NEAREST container and stays that
   * way for write routing: for an action inside a branch inside a loop it is
   * the branch, which is exactly why gating on `locator.kind === "loop"` hid
   * Current Item there. Undefined outside any loop or before a panel opened.
   */
  get currentLoopScope(): LoopScope | undefined {
    return this.currentActionHash !== undefined
      ? this.looping.scopeForAction(this.currentActionHash)
      : undefined;
  }

  /**
   * Gets all actions of a specific group type from the automation's action groups
   * @param groupType - The type of action group to filter by (e.g. "actions", "looping")
   * @returns Array of ActionCandidate objects belonging to the specified group type
   */
  getGroupActions(groupType: ActionGroupType): ActionCandidate[] {
    return collectActionsDeep(
      this.self.actionGroups.filter(
        (group) => group.config.oneofKind === groupType,
      ),
    );
  }

  get groupActions(): ActionCandidate[] {
    return this.getGroupActions("actions");
  }

  /**
   * Gets the index of an action within a specific group type in the automation's action groups
   * @param action - The action to find the index of
   * @param groupType - The type of action group to filter by (e.g. "actions", "looping")
   * @returns The index of the action within the specified group type, or -1 if not found
   */
  private getActionIndex(
    action: ActionCandidate,
    groupType: ActionGroupType = "actions",
  ): number {
    const actions = this.getGroupActions(groupType);

    return actions.findIndex((a) => a.hash === action.hash);
  }

  findActionsWithRecordsList(
    currentAction: ActionCandidate,
    actionGroupType: ActionGroupType = "actions",
  ): ActionCandidate[] {
    const locator = locateActionByHash(
      this.self.actionGroups,
      currentAction.hash,
    );

    if (locator === undefined) {
      return this.findActionsWithRecordsListLegacy(
        currentAction,
        actionGroupType,
      );
    }

    let candidates = this.branches.upstreamActionsOfAction(currentAction.hash);

    // Resolve the ENCLOSING loop (climbing past branch containers), not the
    // nearest container: an action inside a branch that sits inside a loop
    // must still get that loop's source offered first (as Current item).
    const loopInstanceId = locateEnclosingLoop(
      this.self.actionGroups,
      currentAction.hash,
    );
    if (loopInstanceId !== undefined) {
      const loopGroup = this.looping.loopGroupById(loopInstanceId);
      const source = loopGroup?.config.loop.source;
      const sourceInstanceId =
        source?.oneofKind === "recordList"
          ? source.recordList.recordList.oneofKind === "actionReference"
            ? source.recordList.recordList.actionReference.instanceId
            : undefined
          : undefined;

      const sourceAction = this.allActions.find(
        (action) => action.actionReference?.instanceId === sourceInstanceId,
      );

      if (sourceAction !== undefined) {
        candidates = R.uniqBy(
          [sourceAction, ...candidates],
          (action) => action.actionReference?.instanceId,
        );
      }
    }

    return candidates.filter((action) =>
      this.isRecordsListCandidate(action, currentAction),
    );
  }

  /**
   * Legacy top-level-groups-only computation, preserved for actions that
   * aren't found in the tree by hash (e.g. detached/synthetic actions passed
   * by some callers): candidates come from `getGroupActions(actionGroupType)`
   * with index math against `getActionIndex` in that flat list.
   */
  private findActionsWithRecordsListLegacy(
    currentAction: ActionCandidate,
    actionGroupType: ActionGroupType,
  ): ActionCandidate[] {
    const currentActionIndex = this.getActionIndex(
      currentAction,
      actionGroupType,
    );
    const groupActions = this.getGroupActions(actionGroupType);
    let findOneCount = 0;

    if (actionGroupType === "loop") {
      const actions = this.getGroupActions("actions");
      const sourceInstanceId =
        this.looping.source?.oneofKind === "recordList"
          ? this.looping.source?.recordList.recordList.oneofKind ===
            "actionReference"
            ? this.looping.source?.recordList.recordList.actionReference
                .instanceId
            : undefined
          : undefined;

      const loopingSourceAction = actions.find(
        (action) => action.actionReference?.instanceId === sourceInstanceId,
      );
      const findRecordsActions = actions.filter((action) =>
        isFindRecordsOne(action),
      );

      // Add looping source action to the beginning of the actions array
      if (loopingSourceAction) {
        // filter by instanceId, since we add the looping source action to the beginning of the actions array
        const loopingSourceFindRecordsActions = R.uniqBy(
          [loopingSourceAction, ...findRecordsActions],
          (action) => action.actionReference?.instanceId,
        );

        groupActions.unshift(...loopingSourceFindRecordsActions);
        findOneCount = findRecordsActions.length + 1;
      }
    }

    return groupActions.filter((action: ActionCandidate, index) => {
      const normalizedIndex =
        actionGroupType === "loop" ? index - findOneCount : index;
      if (normalizedIndex > currentActionIndex) {
        return false;
      }

      return this.isRecordsListCandidate(action, currentAction);
    });
  }

  /** Shared predicate for both the locator-aware and legacy paths: excludes
   * the action itself and requires a find-records action whose output source
   * type matches what `currentAction` needs. The source's table travels with
   * the list, so a source over another table -- or another solution -- is a
   * legitimate choice. */
  private isRecordsListCandidate(
    action: ActionCandidate,
    currentAction: ActionCandidate,
  ): boolean {
    if (action.hash === currentAction.hash) {
      return false;
    }
    if (action.description.actionId !== ActionLabel.findRecords) {
      return false;
    }

    return (
      action.description.recordListOutput !== undefined &&
      currentAction.description.recordListInput !== undefined &&
      currentAction.description.recordListInput.requiredSourceTypeIds.includes(
        action.description.recordListOutput.sourceTypeId,
      )
    );
  }

  getTimezone(): string {
    return this.self.timezone !== undefined && this.self.timezone !== ""
      ? this.self.timezone
      : "GMT";
  }

  removeInputFromAction(actionHash: string, input: InputCandidate): void {
    this.self.actionGroups = mapActionsDeep(this.self.actionGroups, (action) =>
      action.hash === actionHash
        ? {
            ...action,
            inputs: action.inputs.filter(
              (i: InputCandidate) => i.inputId !== input.inputId,
            ),
          }
        : action,
    );
  }

  get allActions(): ActionCandidate[] {
    return this.automationAllActions(
      this.self as AutomationConfig,
    ) as ActionCandidate[];
  }

  /**
   * Steps consuming the per-automation limit: leaf actions plus every
   * Conditional Logic / Looping block itself. Use this (not allActions.length)
   * for maxActionsPerAutomation gates.
   */
  get totalStepCount(): number {
    return countAutomationStepsDeep(this.self.actionGroups);
  }

  automationAllActions(automation: AutomationConfig): ActionConfig[] {
    // ActionCandidate extends ActionConfig, so the cast narrows only the input shape
    return collectActionsDeep(
      automation.actionGroups as ActionGroupCandidate[],
    );
  }

  get actionsFromGroup(): ActionCandidate[] {
    return this.getGroupActions("actions");
  }
}

const getEmptyAutomation = (
  solutionId: string,
  timezone: string,
): AutomationCandidate => ({
  actionGroups: [
    {
      instanceId: 0,
      config: {
        oneofKind: "actions",
        actions: {
          actions: [],
        },
      },
    },
  ],
  hash: uuid(),
  automationId: "",
  solutionId,
  label: "New Automation",
  description: "",
  userStatus: AutomationConfig_UserStatus.USER_DISABLED,
  trigger: {
    hash: uuid(),
    triggerReference: {
      integrationId: "",
      triggerId: "",
    },
    credentialId: "",
    description: {
      triggerId: "",
      label: "",
      subLabel: "",
      authenticationId: [],
      inputs: [],
      outputs: [],
      conditionFields: [],
      triggerRetrySupport:
        TriggerDescription_TriggerRetrySupport.RETRY_NOT_SUPPORTED,
    },
    inputs: [],
    conditions: {
      operator: ConditionsLogicOperator.OPERATOR_AND,
      group: [],
    },
  },
  actions: [],
  automaticDescription: "",
  timezone,
});
